import { useState } from "react";
import { X } from "lucide-react";
import { money } from "@/lib/currency";
import { addonSum, unitPrice } from "@/lib/addons";
import { useT } from "@/lib/i18n";
import { OptionRow, optionPrice } from "@/components/OptionRow";

const Heading = ({ children }) => <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-[#6B6560]">{children}</p>;

export const AddonSheet = ({ item, cur, accent, onAdd, onClose }) => {
  const { t } = useT();
  const groups = item.variant_groups || [];
  const addons = item.addons || [];
  const [picked, setPicked] = useState([]);
  const [chosen, setChosen] = useState({});
  const toggle = (a) => setPicked((p) => (p.some((x) => x.id === a.id) ? p.filter((x) => x.id !== a.id) : [...p, a]));
  const missing = groups.find((g) => !chosen[g.id]);
  const allChosen = !missing;
  const ordered = groups.map((g) => chosen[g.id] && { ...chosen[g.id], group: g.name });
  const total = (allChosen ? unitPrice(item, ordered) : item.price) + addonSum(picked);

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center" onClick={onClose}>
      <div data-testid="addon-sheet" className="w-full max-w-md bg-[#FAF7F2] rounded-t-3xl p-5 pb-safe max-h-[85dvh] overflow-y-auto no-scrollbar fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-bold text-[#1F1D1B]">{item.name}</h2>
            <p className="text-sm text-[#6B6560] font-mono-j">{groups.length ? `${t("from")} ` : ""}{money(item.price, cur)}</p>
          </div>
          <button data-testid="close-addon-sheet" onClick={onClose} className="text-[#6B6560]"><X className="h-6 w-6" /></button>
        </div>
        {groups.map((g, gi) => (
          <div key={g.id} data-testid={`variant-group-${g.id}`}>
            <Heading>{g.name} · <span style={{ color: chosen[g.id] ? undefined : accent }}>{t("required")}</span></Heading>
            <div className="mt-2 space-y-2">
              {g.options.map((o) => (
                <OptionRow key={o.id} option={o} radio on={chosen[g.id]?.id === o.id} accent={accent} cur={cur} priceLabel={optionPrice(o.price, gi === 0, cur)} onClick={() => setChosen((c) => ({ ...c, [g.id]: o }))} />
              ))}
            </div>
          </div>
        ))}
        {addons.length > 0 && <Heading>{t("extras")} · {t("optional")}</Heading>}
        <div className="mt-2 space-y-2">
          {addons.map((a) => (
            <OptionRow key={a.id} option={a} on={picked.some((x) => x.id === a.id)} accent={accent} cur={cur} priceLabel={`+${money(a.price, cur)}`} onClick={() => toggle(a)} />
          ))}
        </div>
        <button data-testid="addon-sheet-add-button" disabled={!allChosen} onClick={() => onAdd(item, picked, ordered)} className="w-full mt-5 rounded-full py-4 font-bold text-white flex items-center justify-between px-6 transition-transform active:scale-[0.98] disabled:opacity-50" style={{ backgroundColor: accent }}>
          <span>{missing ? t("chooseGroup", { name: missing.name }) : t("addToOrder")}</span>
          <span className="font-mono-j">{allChosen || !groups.length ? money(total, cur) : ""}</span>
        </button>
      </div>
    </div>
  );
};
